import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isGuestActor } from "../helpers/guestMode";
import {
  isDashboardBlockedByPlan,
  resolveEducationPresentation,
  shouldEvaluateEducation,
  shouldSuppressLegacyAutoGuide,
} from "./educationBootGate";
import { EducationLauncherMenu } from "./EducationLauncherMenu";
import { AshleyReleaseBriefPresenter } from "./releaseTour/AshleyReleaseBriefPresenter";
import { createReleaseManager } from "./releaseTour/ReleaseManager";
import { createReleaseTourSession } from "./releaseTour/releaseTourOrchestrator";
import { TourEngineHost } from "./releaseTour/TourEngineHost";
import { TourScreenSignalBridge } from "./releaseTour/TourScreenSignalBridge";
import { WhatsNewPresenter } from "./releaseTour/WhatsNewPresenter";
import { requestWhatsNew } from "../whatsNew/whatsNewStore";
import { APP_RELEASE_ID } from "./config/appRelease";
import {
  log,
  logEducationSkipped,
  logProviderMounted,
  logUserIdAvailable,
} from "./shared/pepBootDebug";
import { GuidedModeProvider } from "./shared/TourEngine/GuidedModeContext";
import { resolveEducationUserId } from "./shared/resolveEducationUserId";
import { CrewPilotToast } from "../components/CrewPilotToast";

/** Beta: auto-start tutorial remains, but off-target interaction dismisses it. Set false before public release. */
const BETA_TUTORIAL_DISMISSIBLE = true;
const TUTORIAL_SKIPPED_MESSAGE =
  "Tutorial skipped. You can restart it anytime from the Help menu.";
const TUTORIAL_SKIPPED_TOAST_MS = 2500;

const ProductEducationContext = createContext(null);

export function useProductEducation() {
  const context = useContext(ProductEducationContext);
  if (!context) {
    throw new Error("useProductEducation must be used within ProductEducationProvider");
  }
  return context;
}

export function useProductEducationOptional() {
  return useContext(ProductEducationContext);
}

function applyPresentation(presentation, { releaseManager, setActiveView, refreshState }) {
  log(`Applying presentation: ${presentation.type}`);

  if (presentation.type === "brief") {
    releaseManager.markBriefShown(presentation.plan.releaseId);
    refreshState();
    setActiveView("brief");
    return;
  }

  if (presentation.type === "required_tour") {
    setActiveView("required_tour");
    return;
  }

  if (presentation.type === "whats_new_chip") {
    setActiveView("chip");
    return;
  }

  logEducationSkipped(
    `presentation resolved to none (plan reason: ${presentation.plan?.reason ?? "unknown"})`,
  );
}

export function ProductEducationProvider({
  children,
  actor,
  screen,
  authLoading,
  onOpenUserPreferences,
}) {
  const userId = resolveEducationUserId(actor);
  const autoEvaluatedRef = useRef(false);
  const evaluatedForUserIdRef = useRef(null);
  const tutorialDismissedThisSessionRef = useRef(false);
  const releaseManager = useMemo(
    () => createReleaseManager(userId),
    [userId],
  );

  const [educationState, setEducationState] = useState(() => releaseManager.getState());
  const [activeView, setActiveView] = useState("none");
  const [evaluation, setEvaluation] = useState(null);
  const [showLauncherMenu, setShowLauncherMenu] = useState(false);
  const [tourSession, setTourSession] = useState(null);
  const [guidedModeState, setGuidedModeState] = useState({
    isActive: false,
    stepId: null,
    stepPhase: null,
    scopes: [],
  });
  const [educationBootComplete, setEducationBootComplete] = useState(false);
  const [showTutorialSkippedToast, setShowTutorialSkippedToast] = useState(false);

  useEffect(() => {
    logProviderMounted({
      userId,
      screen,
      authLoading,
      hasActor: Boolean(actor),
    });
  }, [userId, screen, authLoading, actor]);

  useEffect(() => {
    setEducationState(releaseManager.getState());
  }, [releaseManager]);

  useEffect(() => {
    if (
      evaluatedForUserIdRef.current &&
      userId &&
      evaluatedForUserIdRef.current !== userId
    ) {
      autoEvaluatedRef.current = false;
      evaluatedForUserIdRef.current = null;
      setEducationBootComplete(false);
    }
  }, [userId]);

  const activePlan = evaluation?.plan || null;
  const shouldSuppressLegacyAutoGuideFlag = useMemo(
    () =>
      shouldSuppressLegacyAutoGuide({
        educationBootComplete,
        activeView,
        showLauncherMenu,
        activePlan,
        educationState,
        releaseId: APP_RELEASE_ID,
      }),
    [
      educationBootComplete,
      activeView,
      showLauncherMenu,
      activePlan,
      educationState,
    ],
  );
  // During the live tour, TourEngine backdrop panels own interaction lock so
  // highlighted targets (e.g. guide-ashley-fab) can still receive pointer events.
  const isDashboardBlocked =
    isDashboardBlockedByPlan(activePlan) &&
    activeView !== "none" &&
    activeView !== "whats_new" &&
    activeView !== "tour";

  const refreshState = useCallback(() => {
    const next = releaseManager.getState();
    setEducationState(next);
    return next;
  }, [releaseManager]);

  const closeAllViews = useCallback(() => {
    setActiveView("none");
    setShowLauncherMenu(false);
    setTourSession(null);
    setGuidedModeState({
      isActive: false,
      stepId: null,
      stepPhase: null,
      scopes: [],
    });
    setEvaluation((current) =>
      current
        ? {
            ...current,
            plan: {
              ...current.plan,
              blockDashboard: false,
              showRequiredTour: false,
              showBrief: false,
            },
          }
        : current,
    );
  }, []);

  const openWhatsNew = useCallback(() => {
    releaseManager.openWhatsNewManual();
    refreshState();
    setShowLauncherMenu(false);
    setActiveView("whats_new");
  }, [releaseManager, refreshState]);

  const openEducationMenu = useCallback(() => {
    setShowLauncherMenu(true);
  }, []);

  const replayLatestRelease = useCallback(() => {
    const result = releaseManager.replayLatestRelease();
    setEvaluation(result);
    setShowLauncherMenu(false);
    const presentation = resolveEducationPresentation(
      result.plan,
      result.state.settings,
    );
    applyPresentation(presentation, {
      releaseManager,
      setActiveView,
      refreshState,
    });
  }, [releaseManager, refreshState]);

  const replayAshleyBriefing = useCallback(() => {
    replayLatestRelease();
  }, [replayLatestRelease]);

  const handleShowMe = useCallback(() => {
    releaseManager.handleBriefChoice("show_me");
    refreshState();
    setActiveView("whats_new");
  }, [releaseManager, refreshState]);

  const handleJustTellMe = useCallback(() => {
    releaseManager.handleBriefChoice("just_tell_me");
    refreshState();
    setActiveView("whats_new");
  }, [releaseManager, refreshState]);

  const beginReleaseTour = useCallback(
    ({ allowSkip } = {}) => {
      const session = createReleaseTourSession({
        releaseId: APP_RELEASE_ID,
        featureIds: activePlan?.tourFeatureIds || [],
        allowSkip: allowSkip ?? !activePlan?.requiredTour,
        allowHardwareBackDismiss: BETA_TUTORIAL_DISMISSIBLE
          ? true
          : (activePlan?.allowHardwareBackDismiss ?? false),
        onComplete: () => {
          releaseManager.completeTourPlaceholder();
          refreshState();
          closeAllViews();
        },
        onCancel: () => {
          if (activePlan?.requiredTour && !BETA_TUTORIAL_DISMISSIBLE) {
            return;
          }
          if (BETA_TUTORIAL_DISMISSIBLE && activePlan?.requiredTour) {
            tutorialDismissedThisSessionRef.current = true;
            setShowTutorialSkippedToast(true);
          }
          closeAllViews();
        },
      });
      setTourSession(session);
      setActiveView("tour");
    },
    [releaseManager, refreshState, activePlan, closeAllViews],
  );

  const handleContinue = useCallback(() => {
    releaseManager.handleBriefChoice("continue");
    refreshState();
    beginReleaseTour({ allowSkip: false });
  }, [releaseManager, refreshState, beginReleaseTour]);

  const handleSkip = useCallback(() => {
    releaseManager.handleBriefChoice("skip");
    refreshState();
    closeAllViews();
  }, [releaseManager, refreshState, closeAllViews]);

  const updateSettings = useCallback(
    (partialSettings) => {
      releaseManager.updateSettings(partialSettings);
      refreshState();
    },
    [releaseManager, refreshState],
  );

  const dismissWhatsNewChip = useCallback(() => {
    releaseManager.dismissWhatsNewChip();
    refreshState();
  }, [releaseManager, refreshState]);

  const startTourFromWhatsNew = useCallback(() => {
    beginReleaseTour({ allowSkip: !activePlan?.requiredTour });
  }, [beginReleaseTour, activePlan]);

  useEffect(() => {
    if (activeView !== "required_tour" || tourSession) {
      return;
    }
    if (BETA_TUTORIAL_DISMISSIBLE && tutorialDismissedThisSessionRef.current) {
      closeAllViews();
      return;
    }
    beginReleaseTour({ allowSkip: false });
  }, [activeView, tourSession, beginReleaseTour, closeAllViews]);

  useEffect(() => {
    if (autoEvaluatedRef.current) {
      setEducationBootComplete(true);
      return;
    }

    if (
      !shouldEvaluateEducation({
        screen,
        authLoading,
        hasUser: Boolean(actor),
        userId,
        autoEvaluatedRef,
      })
    ) {
      return;
    }

    logUserIdAvailable(userId);

    const result = releaseManager.evaluate({
      isGuest: isGuestActor(actor),
      context: "auto",
    });
    setEvaluation(result);

    const presentation = resolveEducationPresentation(
      result.plan,
      result.state.settings,
    );
    applyPresentation(presentation, {
      releaseManager,
      setActiveView,
      refreshState,
    });

    autoEvaluatedRef.current = true;
    evaluatedForUserIdRef.current = userId;
    setEducationBootComplete(true);
  }, [actor, authLoading, screen, releaseManager, refreshState, userId]);

  const whatsNewPayload = useMemo(
    () => releaseManager.getWhatsNewPayload(APP_RELEASE_ID),
    [releaseManager, educationState],
  );

  const releaseRecord = educationState.releases[APP_RELEASE_ID];
  const showWhatsNewChip =
    activeView === "chip" &&
    educationState.settings.alwaysShowWhatsNew &&
    !releaseRecord?.whatsNewChipDismissed &&
    !releaseRecord?.whatsNewOpened;

  const contextValue = useMemo(
    () => ({
      educationState,
      evaluation,
      activePlan,
      isEducationActive: activeView !== "none" || showLauncherMenu,
      isDashboardBlocked,
      shouldSuppressLegacyAutoGuide: shouldSuppressLegacyAutoGuideFlag,
      educationBootComplete,
      openEducationMenu,
      openWhatsNew,
      replayLatestRelease,
      replayAshleyBriefing,
      updateSettings,
      dismissWhatsNewChip,
      getWhatsNewPayload: () => releaseManager.getWhatsNewPayload(APP_RELEASE_ID),
      settings: educationState.settings,
    }),
    [
      educationState,
      evaluation,
      activePlan,
      activeView,
      showLauncherMenu,
      isDashboardBlocked,
      shouldSuppressLegacyAutoGuideFlag,
      educationBootComplete,
      openEducationMenu,
      openWhatsNew,
      replayLatestRelease,
      replayAshleyBriefing,
      updateSettings,
      dismissWhatsNewChip,
      releaseManager,
    ],
  );

  return (
    <ProductEducationContext.Provider value={contextValue}>
      <GuidedModeProvider
        isActive={guidedModeState.isActive}
        stepId={guidedModeState.stepId}
        stepPhase={guidedModeState.stepPhase}
        scopes={guidedModeState.scopes}
      >
        <div className={isDashboardBlocked ? "pep-dashboard-blocked" : undefined}>
          {children}
        </div>
        <TourScreenSignalBridge
          screen={screen}
          isGuidedModeActive={guidedModeState.isActive}
        />
      </GuidedModeProvider>

      {activeView === "brief" && evaluation?.briefScript ? (
        <AshleyReleaseBriefPresenter
          briefScript={evaluation.briefScript}
          briefUi={activePlan?.briefUi}
          onShowMe={handleShowMe}
          onJustTellMe={handleJustTellMe}
          onSkip={handleSkip}
          onContinue={handleContinue}
        />
      ) : null}

      {activeView === "tour" && tourSession ? (
        <TourEngineHost
          releaseId={tourSession.releaseId}
          featureIds={tourSession.featureIds}
          allowSkip={tourSession.allowSkip}
          allowHardwareBackDismiss={tourSession.allowHardwareBackDismiss}
          allowDismissOnOffTargetInteraction={BETA_TUTORIAL_DISMISSIBLE}
          estimatedDurationMinutes={tourSession.estimatedDurationMinutes}
          onComplete={tourSession.onComplete}
          onCancel={tourSession.onCancel}
          onGuidedModeChange={setGuidedModeState}
        />
      ) : null}

      {activeView === "whats_new" ? (
        <WhatsNewPresenter
          payload={whatsNewPayload}
          onClose={closeAllViews}
          onStartTour={startTourFromWhatsNew}
          allowClose
          allowHardwareBackDismiss
        />
      ) : null}

      {showWhatsNewChip ? (
        <div className="pep-whats-new-chip" role="status">
          <p className="pep-whats-new-chip__text">
            See what&apos;s new in {whatsNewPayload.meta?.title || "CrewPilot"}
          </p>
          <div className="pep-whats-new-chip__actions">
            <button type="button" className="pep-btn pep-btn--primary" onClick={openWhatsNew}>
              Open
            </button>
            <button
              type="button"
              className="pep-btn pep-btn--ghost"
              onClick={dismissWhatsNewChip}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {showLauncherMenu ? (
        <EducationLauncherMenu
          onClose={() => setShowLauncherMenu(false)}
          // Same modern What's New as Settings (WhatsNewHost), not the legacy release tour.
          onOpenWhatsNew={requestWhatsNew}
          onOpenUserPreferences={onOpenUserPreferences}
        />
      ) : null}

      {showTutorialSkippedToast ? (
        <CrewPilotToast
          message={TUTORIAL_SKIPPED_MESSAGE}
          autoDismissMs={TUTORIAL_SKIPPED_TOAST_MS}
          dontShowAgainLabel="Don't show this again"
          onDismiss={({ dontShowAgain } = {}) => {
            if (dontShowAgain) {
              releaseManager.completeTourPlaceholder();
              refreshState();
            }
            setShowTutorialSkippedToast(false);
          }}
        />
      ) : null}
    </ProductEducationContext.Provider>
  );
}
